import React, { useState, useCallback, useRef } from 'react';
import confetti from 'canvas-confetti';

/**
 * Hook for submitting lead data from landing page forms.
 */
export default function useLeadSubmit({ brandId, blockType, blockId = null, pageId = null, source = null } = {}) {
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState(null);
    const honeypotRef = useRef('');

    const submit = useCallback(async (formData) => {
        setSubmitting(true);
        setError(null);

        try {
            const payload = {
                brand_id: brandId,
                block_type: blockType,
                block_id: blockId,
                page_id: pageId,
                source: source,
                name: formData.name || formData.nombre || null,
                email: formData.email || null,
                phone: formData.phone || formData.telefono || formData.tel || null,
                data: formData,
                _honeypot: honeypotRef.current || '',
            };

            const res = await fetch('/api/leads', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content || '',
                    'Accept': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            const json = await res.json();

            if (!res.ok) {
                setError(json.message || 'Error al enviar. Intenta de nuevo.');
                setSubmitting(false);
                return false;
            }

            setSubmitted(true);
            setSubmitting(false);
            confetti({
                particleCount: 80,
                spread: 60,
                origin: { y: 0.7 },
                colors: ['#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981'],
                disableForReducedMotion: true,
            });
            return true;
        } catch (e) {
            setError('Error de conexion. Intenta de nuevo.');
            setSubmitting(false);
            return false;
        }
    }, [brandId, blockType, blockId, pageId, source]);

    const reset = useCallback(() => {
        setSubmitted(false);
        setError(null);
    }, []);

    const setHoneypot = useCallback((val) => { honeypotRef.current = val; }, []);

    return { submit, submitting, submitted, error, reset, setHoneypot };
}

/** Hidden honeypot field — bots fill it, humans don't see it. */
export function Honeypot({ onFill }) {
    return (
        <div style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }} aria-hidden="true" tabIndex={-1}>
            <input type="text" name="_honeypot" autoComplete="off" tabIndex={-1}
                onChange={e => onFill?.(e.target.value)} />
        </div>
    );
}

/** Error message display for lead submission failures. */
export function LeadError({ error }) {
    if (!error) return null;
    return (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-2 mt-3">
            {error}
        </div>
    );
}
